import moment, {type Moment} from 'moment-timezone';

import type {AutomaticPricing} from 'web-app/react/profile/child-profile/plans-and-invoices/ad-hoc-purchases/hooks/use-get-automatic-pricing-for-purchases';
import type {Child as PowerPageChild} from 'web-app/react/routes/account/institution/finance/power-page/types';
import {head, isNonEmpty} from 'web-app/util/non-empty-array';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {isEmpty} from 'web-app/util/array';
import {isWarnings} from 'web-app/util/warnable';
import * as Failable from 'web-app/util/failable';

import type * as Types from '../types';
import {transformAutomaticPricingToPricingGroupForChild} from './pricing-group-transformers';
import {transformPowerPagePlanToPlan, transformBillingFrequencyForBillPayer} from './plan-transformers';

export const transformPowerPageChildToChild = ({
    child,
    automaticPricings,
    automaticRuleGroupId,
}: {
    child: PowerPageChild;
    automaticPricings: Array<AutomaticPricing>;
    automaticRuleGroupId: null | string;
}): Failable.Failable<{child: Types.Child; customDiscounts: Types.DiscountCustom[]} | null, string> => {
    const billPayersBillingFrequencies = child.billPayers.map(billPayer => billPayer.billingFrequency).filter(hasValue);
    const billPayers = Failable.all(
        child.billPayers.map(billPayer => {
            const billingFrequencyWarnable = transformBillingFrequencyForBillPayer(billPayer.billingFrequency);
            if (isWarnings(billingFrequencyWarnable) && isNonEmpty(billingFrequencyWarnable.warnings)) {
                return Failable.fail(head(billingFrequencyWarnable.warnings).warning);
            }
            return Failable.resolve({
                id: billPayer.billPayer.billPayerId,
                billingFrequency: billingFrequencyWarnable.value ?? undefined,
            });
        }),
    );

    const plansAndCustomDiscounts = (() => {
        const transformedPlansAndCustomDiscounts = child.plans.map(plan =>
            transformPowerPagePlanToPlan(plan, billPayersBillingFrequencies),
        );
        // Check if any plan transformation failed
        const failedPlan = transformedPlansAndCustomDiscounts.find(Failable.isFailed);
        if (failedPlan) {
            return Failable.fail(failedPlan.error);
        }

        // Filter out null values and extract successful results
        return Failable.resolve(
            transformedPlansAndCustomDiscounts
                .filter(Failable.isResolved) // there should be no failed plans at this point
                .map(result => result.value)
                .filter(hasValue),
        );
    })();

    if (Failable.isFailed(plansAndCustomDiscounts)) {
        return plansAndCustomDiscounts;
    }

    const flatPlansAndCustomDiscounts = {
        plans: plansAndCustomDiscounts.value.map(plansAndCustomDiscounts => plansAndCustomDiscounts.plan),
        customDiscounts: plansAndCustomDiscounts.value.flatMap(
            plansAndCustomDiscounts => plansAndCustomDiscounts.customDiscounts,
        ),
    };

    if (isEmpty(automaticPricings) || !hasValue(automaticRuleGroupId) || !hasValue(child.child.birthday)) {
        if (Failable.isFailed(billPayers)) {
            return Failable.fail(billPayers.error.join(', '));
        }

        return Failable.resolve({
            child: {
                id: child.child.id,
                fullName: child.child.name.fullName,
                profileImageUrl: child.child.profileImage?.url,
                billPayers: billPayers.value,
                pricingGroups: [],
                hasPlans: isNonEmpty(child.plans),
                plans: flatPlansAndCustomDiscounts.plans,
                startDate: moment(child.child.startDate),
                birthday: child.child.birthday,
                lastDay: child.child.lastDay,
            },
            customDiscounts: flatPlansAndCustomDiscounts.customDiscounts,
        });
    }

    const pricingForChild = automaticPricings.find(pricing => pricing.childId === child.child.id);
    if (!hasValue(pricingForChild)) {
        // TODO: This is triggered in the real world. See GROWTH-295
        return Failable.resolve(null);
    }

    const pricingGroups = transformAutomaticPricingToPricingGroupForChild(pricingForChild, automaticRuleGroupId);
    if (!hasValue(pricingGroups)) {
        return Failable.fail('Failed to transform pricing groups.');
    }

    if (!isNonEmpty(pricingGroups)) {
        return Failable.fail('No pricing groups for child.');
    }

    if (Failable.isFailed(billPayers)) {
        return Failable.fail(billPayers.error.join(', '));
    }

    return Failable.resolve({
        child: {
            id: child.child.id,
            fullName: child.child.name.fullName,
            profileImageUrl: child.child.profileImage?.url,
            billPayers: billPayers.value,
            pricingGroups,
            hasPlans: isNonEmpty(child.plans),
            plans: flatPlansAndCustomDiscounts.plans,
            startDate: moment(child.child.startDate),
            birthday: child.child.birthday,
            lastDay: child.child.lastDay,
        },
        customDiscounts: flatPlansAndCustomDiscounts.customDiscounts,
    });
};

/**
 * Patches in the `firstUnbilledDate` field into the plans when the plan has one.
 */
export const patchUnbilledDatesIntoPlans = (
    child: Types.Child,
    firstUnbilledDatesForPlanIds: Map<string, Moment>,
): Types.Child => ({
    ...child,
    plans: child.plans.map(plan => {
        const firstUnbilledDate = firstUnbilledDatesForPlanIds.get(plan.id);
        if (!hasValue(firstUnbilledDate)) {
            return plan;
        }
        return {
            ...plan,
            firstUnbilledDate,
        };
    }),
});
